import { z } from 'zod';

const optionalCompanyText = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().min(1).max(255).optional(),
);

const optionalRemarks = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().max(2000).optional(),
);

export const NOTE_REASONS = [
  'Claim',
  'Quantity Difference',
  'Provisional Difference',
  'Other',
] as const;

// Each sell-side note categorises its lines (Launchpad parity). A Debit line
// typed "Demurrage" is computed from Days × Rate/Day rather than a flat Amount.
export const CREDIT_ADJUSTMENT_TYPES = [
  'Weight adjustment',
  'Price adjustment',
  'Quality adjustment',
  'Overpayment',
  'Other',
] as const;
export const DEBIT_ADJUSTMENT_TYPES = [
  'Lump sum',
  'Demurrage',
  'Price adjustment',
  'Weight adjustment',
  'Other',
] as const;

export function adjustmentTypesFor(noteType: 'Credit' | 'Debit'): readonly string[] {
  return noteType === 'Credit' ? CREDIT_ADJUSTMENT_TYPES : DEBIT_ADJUSTMENT_TYPES;
}

export const DEMURRAGE_TYPE = 'Demurrage';

// Presentational status options on the Credit Note form (Launchpad parity).
// Like the Provisional dialog, the backend does not accept a status on create
// (notes are always Draft), so this field is NOT forwarded on submit.
export const ADJUSTMENT_STATUS_OPTIONS = ['Draft', 'Paid', 'Cancelled'] as const;

// Launchpad parity: the buy side (Credit + Debit) uses the simple
// supplier-invoice layout (Company / Reason / Invoice date / Status / Notes).
// Both sell-side notes itemise the charge with adjustment lines — Debit as a
// demurrage breakdown, Credit as categorised credit lines.
export function usesSimpleLayout(_noteType: 'Credit' | 'Debit', side: 'buy' | 'sell'): boolean {
  return side === 'buy';
}

// Held as the native date input's YYYY-MM-DD string (and forwarded verbatim on
// the wire); a blank input settles to undefined so the optional supplier
// invoice date can be left empty.
const optionalDate = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date in YYYY-MM-DD form')
    .optional(),
);

export const ADJUSTMENT_LINE_UNITS = ['days', 'mt', 'each', 'other'] as const;

// Settlement methods differ per note type (Launchpad parity): a Credit can be
// offset or refunded; a Debit is payable or offset. The union is what the wire
// accepts; the dialog shows the per-type subset.
export const CREDIT_SETTLEMENT_METHODS = [
  'Offset against next invoice',
  'Refund by TT',
  'Other',
] as const;
export const DEBIT_SETTLEMENT_METHODS = [
  'Payable by TT',
  'Offset against next invoice',
  'Other',
] as const;
export const SETTLEMENT_METHODS = [
  'Offset against next invoice',
  'Refund by TT',
  'Payable by TT',
  'Other',
] as const;

export function settlementMethodsFor(noteType: 'Credit' | 'Debit'): readonly string[] {
  return noteType === 'Credit' ? CREDIT_SETTLEMENT_METHODS : DEBIT_SETTLEMENT_METHODS;
}

// Mirrors the backend tolerance so the dialog disables Save before the request fails server-side.
export const ADJUSTMENT_LINES_SUM_TOLERANCE = 0.01;

// A line's total is always quantity × unit_price (read-only in the form,
// recomputed on submit) — for a 'days' unit that's demurrage days × rate/day.
export function lineTotal(quantity: unknown, unitPrice: unknown): number {
  return Math.round((Number(quantity) || 0) * (Number(unitPrice) || 0) * 100) / 100;
}

export const ADJUSTMENT_LINE_FIELDS = z.object({
  type: z.string().optional(),
  description: z.string().min(1, 'Description is required').max(500),
  quantity: z.coerce.number().positive(),
  unit: z.enum(ADJUSTMENT_LINE_UNITS),
  unit_price: z.coerce.number().nonnegative(),
  line_total: z.coerce.number().nonnegative(),
});

export type AdjustmentLineValues = z.infer<typeof ADJUSTMENT_LINE_FIELDS>;

// The buy-side notes pick from NOTE_REASONS; the sell-side notes take a
// free-text reason (Launchpad parity), so the field accepts any non-empty
// string and the dialog constrains the buy-side set.
const optionalNoteReason = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().max(255).optional(),
);

const optionalSettlementMethod = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.enum(SETTLEMENT_METHODS).optional(),
);

const optionalPaymentTerms = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().max(500).optional(),
);

// A blank / cleared numeric input settles to undefined rather than NaN so the
// declared new value stays optional until typed.
const optionalPositiveNumber = z.preprocess(
  (v) => (v === '' || v === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v),
  z.coerce.number().positive().optional(),
);

export const ADJUSTMENT_NOTE_SUBMIT_FIELDS = z.object({
  adjusts_invoice_id: z.string().min(1, 'Pick a parent invoice'),
  invoice_number: z.string().min(1, 'Invoice number is required'),
  currency: z.enum(['USD', 'EUR']),
  total_amount: z.coerce.number().positive(),
  // The declared change behind the note: 'none' keeps the manual amount;
  // price / weight recomputes the amount from the delta against the side's
  // current core values (mirrored server-side, which is the enforcement).
  change_driver: z.enum(['none', 'price', 'weight']).optional(),
  change_new_value: optionalPositiveNumber,
  vat_amount: z.coerce.number().nonnegative(),
  issue_date: optionalDate,
  original_due_date: z.coerce.date(),
  status: z.enum(ADJUSTMENT_STATUS_OPTIONS).optional(),
  note_reason: optionalNoteReason,
  settlement_method: optionalSettlementMethod,
  // Debit-only free text, prepended to remarks on submit (Launchpad parity).
  payment_terms: optionalPaymentTerms,
  company_name: optionalCompanyText,
  issuer_company_name: optionalCompanyText,
  remarks: optionalRemarks,
  adjustment_lines: z.array(ADJUSTMENT_LINE_FIELDS).max(50).optional(),
});

export type AdjustmentNoteSubmitValues = z.infer<typeof ADJUSTMENT_NOTE_SUBMIT_FIELDS>;
